import * as L from 'leaflet';
import find from 'lodash/find';
import { addClassNames } from './lib/utils/dom';
import { Autohide } from './lib/Autohide';
/**
 * The layerlist control default options.
 */
const CONTROL_LAYERLIST_OPTIONS_DEFAULT = {
    position: 'topright',
    groups: [],
    exclude: [],
};
/**
 * The layerlist control.
 */
export class ControlLayerlist extends L.Control {
    /**
     * Create a new layerlist control instance.
     * @param options - The layerlist control options.
     * @param widget
     */
    constructor(options, widget) {
        super(Object.assign(Object.assign({}, CONTROL_LAYERLIST_OPTIONS_DEFAULT), options));
        if (typeof this.options.exclude === 'string') {
            this.options.exclude = [this.options.exclude];
        }
        this.widget = widget;
    }
    /**
     * The hook to execute when the control is added to the map.
     * @param map - The leaflet map instance the control is added to.
     * @returns The control's container element.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onAdd(map) {
        const container = document.createElement('div');
        const containerClassNames = [
            ControlLayerlist.className,
            'maap_c-control',
            'maap_c-floating',
            'maap_c-right-attached',
        ];
        addClassNames(container, containerClassNames);
        this.autohide = new Autohide({
            element: container,
            leafletMap: map,
        });
        this._container = container;
        this._map = map;
        this.addEventListeners();
        this.update();
        return container;
    }
    /**
     * The hook to execute when the control is removed from the map.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onRemove() {
        var _a;
        (_a = this.autohide) === null || _a === void 0 ? void 0 : _a.destroy();
        this.removeEventListeners();
    }
    /**
     * Add event listeners on the container and the map itself.
     */
    addEventListeners() {
        if (this._container) {
            this._container.addEventListener('click', this.onClick.bind(this));
        }
        this.widget.layers.on('LAYERS_LAYER_ADDED LAYERS_LAYER_REMOVED LAYERS_LAYER_SHOWN', this.update, this);
    }
    /**
     * Remove event listeners set on the container and the map.
     */
    removeEventListeners() {
        if (this._container) {
            this._container.removeEventListener('click', this.onClick);
        }
        this.widget.layers.off('LAYERS_LAYER_ADDED LAYERS_LAYER_REMOVED LAYERS_LAYER_SHOWN', this.update, this);
    }
    /**
     * Click event listener.
     * @param event - The click event.
     */
    onClick(event) {
        const target = event.target;
        if (target.tagName !== 'INPUT')
            return;
        const groupName = target.name;
        const layerName = target.value;
        const elForm = this.widget.querySelector(`.${ControlLayerlist.className}__form[data-group-name="${groupName}"]`);
        if (elForm) {
            this.toggleCheckedClasses(elForm);
        }
        const layer = this.widget.layers.getLayer(layerName);
        const group = find(this.options.groups, { name: groupName });
        if (group) {
            if (group.type === 'exclusive') {
                // ts-ignore
                if (!layer._map) {
                    const layers = this.widget.layers.getLayersByGroup(groupName);
                    layers.forEach((layer) => this.widget.layers.hideLayer(layer.options.name));
                    this.widget.layers.showLayer(layerName);
                }
            }
            else {
                this.widget.layers.toggleLayer(layerName);
            }
        }
    }
    /**
     * Toggle the checked classes based on the input's value.
     * @param form - The form where the classes should be applied.
     */
    toggleCheckedClasses(form) {
        const layerContainers = form.querySelectorAll(`.${ControlLayerlist.className}__layer`);
        layerContainers.forEach((layerContainer) => {
            const checkedInput = layerContainer.querySelector('input:checked');
            layerContainer.classList.toggle('is-checked', checkedInput !== null);
        });
    }
    /**
     * Update the layerlist.
     */
    update() {
        if (!this._map) {
            return;
        }
        const groupNames = this.widget.layers
            .getGroups()
            .filter((groupName) => {
            return !!(groupName && this.options.exclude.indexOf(groupName) === -1);
        });
        let groups = groupNames.map((groupName) => {
            // Get all layers from the group.
            const layers = this.widget.layers.getLayersByGroup(groupName);
            // Only show layers with a title.
            const layersFiltered = layers.filter((layer) => layer.options.title);
            // We don't need the whole layer for the control.
            const layersMapped = layersFiltered.map((layer) => {
                var _a;
                return ({
                    name: layer.options.name,
                    title: layer.options.title,
                    description: layer.options.description,
                    isChecked: (_a = this._map) === null || _a === void 0 ? void 0 : _a._layers.hasOwnProperty(layer._leaflet_id),
                });
            });
            // Find group options if present.
            const group = find(this.options.groups, { name: groupName }) || undefined;
            return {
                name: groupName,
                title: group ? group.title : undefined,
                description: group ? group.description : undefined,
                type: group ? group.type : 'inclusive',
                layers: layersMapped,
            };
        });
        groups = groups.filter((group) => group.layers.length > 0);
        const listContainer = document.createElement('ul');
        addClassNames(listContainer, [
            `${ControlLayerlist.className}__groups`,
            'maap_c-list-unstyled',
        ]);
        groups.forEach((group) => {
            const groupContainer = document.createElement('li');
            addClassNames(groupContainer, [
                `${ControlLayerlist.className}__group`,
                `${ControlLayerlist.className}__group--` + group.type,
            ]);
            listContainer.appendChild(groupContainer);
            if (group.title) {
                const groupTitle = document.createElement('strong');
                groupTitle.innerHTML = group.title;
                groupTitle.title = group.description || group.title;
                groupTitle.classList.add(`${ControlLayerlist.className}__title`);
                groupContainer.appendChild(groupTitle);
            }
            const groupForm = document.createElement('form');
            groupForm.classList.add(`${ControlLayerlist.className}__form`);
            groupForm.setAttribute('data-group-name', group.name);
            groupContainer.appendChild(groupForm);
            const groupList = document.createElement('ul');
            addClassNames(groupList, [
                `${ControlLayerlist.className}__layers`,
                'maap_c-list-unstyled',
            ]);
            groupForm.appendChild(groupList);
            group.layers.forEach((layer) => {
                const layerContainer = document.createElement('li');
                const layerContainerClassNames = [
                    `${ControlLayerlist.className}__layer`,
                ];
                if (layer.isChecked)
                    layerContainerClassNames.push('is-checked');
                layerContainer.setAttribute('data-test-layerlist-active-layer', layer.isChecked ? 'true' : 'false');
                addClassNames(layerContainer, layerContainerClassNames);
                groupList.appendChild(layerContainer);
                const layerLabel = document.createElement('label');
                layerLabel.classList.add(`${ControlLayerlist.className}__label`);
                layerContainer.appendChild(layerLabel);
                const layerInput = document.createElement('input');
                layerInput.name = group.name;
                layerInput.type = group.type === 'exclusive' ? 'radio' : 'checkbox';
                layerInput.value = layer.name;
                layerInput.checked =
                    layer.isChecked !== undefined ? layer.isChecked : false;
                layerInput.classList.add(`${ControlLayerlist.className}__input`);
                layerLabel.appendChild(layerInput);
                const layerIconContainer = document.createElement('span');
                addClassNames(layerIconContainer, [
                    'maap_c-icon-container',
                    `${ControlLayerlist.className}__icon-container`,
                ]);
                layerLabel.appendChild(layerIconContainer);
                const layerIcon = document.createElement('span');
                addClassNames(layerIcon, [
                    'maap_c-icon-container__icon',
                    `${ControlLayerlist.className}__icon`,
                    `${ControlLayerlist.className}__icon--${layerInput.type}`,
                ]);
                layerIconContainer.appendChild(layerIcon);
                if (layer.title) {
                    const labelText = document.createTextNode(layer.title);
                    layerLabel.appendChild(labelText);
                }
            });
        });
        if (this._container) {
            this._container.innerHTML = '';
            this._container.appendChild(listContainer);
        }
    }
}
/**
 * THe layerlist control's CSS class.
 */
ControlLayerlist.className = 'maap_p-control-layerlist';
//# sourceMappingURL=ControlLayerlist.js.map